import {
  Chart as ChartJS,
  Plugin,
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

// import { chartTimePeriod } from '../../../../../config/enum';
import currentTimeZone from '../../../../../config/timeZone';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
// import CustomToggleButton from '../../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../../shared-functions/ChartFunctions';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerV2 from '../../../../../utils/api/end-consumer-v2';

/* ------------------ ChartJS Register ------------------ */
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

/* ------------------ Hover Center Line Plugin ------------------ */
const verticalHoverLinePlugin: Plugin<'line'> = {
  id: 'verticalHoverLine',

  /**
   * afterDatasetsDraw
   * @param {any} chart - The chart
   * @returns {any} The result
   */
  afterDatasetsDraw: (chart: Chart) => {
    const { tooltip } = chart;

    if (!tooltip || !tooltip.getActiveElements().length) return;

    const { ctx } = chart;
    const activePoint = tooltip.getActiveElements()[0];
    const { x } = activePoint.element;

    const topY = chart.scales.y.top;
    const bottomY = chart.scales.y.bottom;

    ctx.save();

    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#00A79D';
    ctx.lineCap = 'butt'; // straight line (no caps)
    ctx.moveTo(x, topY); // ✅ from top
    ctx.lineTo(x, bottomY); // ✅ to bottom
    ctx.stroke();

    ctx.restore();
  },
};

ChartJS.register(verticalHoverLinePlugin);

ChartJS.register(verticalHoverLinePlugin);

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

interface RevenuePowerSessionsResponse {
  userCustomId: string;
  totalSessions: number;
  totalRevenue: number;
  totalPowerConsumedInKwh: number;
}

/**
 * Component to display revenue vs power consumed chart
 * @returns {any} The result
 */
export default function SpendChart() {
  const { t, i18n } = useTranslation('endConsumer');
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  // State to keep selected date range
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [revenuePowerSessions, setRevenuePowerSessions] =
    useState<RevenuePowerSessionsResponse | null>(null);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: t('revenue'),
        data: powerConsumedData.map((d) => d.totalRevenue),
        borderColor: '#FD4895',
        /**
         * Background
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @returns {any} The result
         */
        backgroundColor: () => {
          return 'rgba(255, 255, 255, 1)'; // Default color
        },
        borderColor: '#00A79D', // ✅ green border
        borderWidth: 1,
        titleColor: '#212529',
        bodyColor: '#212529',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Consumption
           * @returns {any} The result
           */
          title: () => t('revenue'),
          /**
           * Consumption values
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            return `${ToFixedValue(context.raw)} ${t('SAR')}`;
          },
        },
      },
    },

    interaction: {
      mode: 'index' as const,
      intersect: false,
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: false,
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await endConsumerV2.fetchRevenuePowerSessions(
        selectedEndConsumer?.userCustomId as string,
        {
          timePeriod: range,
          timeZone: currentTimeZone,
          type: endConsumerType,
        },
      );

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      const result = response.data?.results;
      if (range === 'last12months' && response.status === 200) {
        setPowerConsumedData(fillMissingMonthsGeneric(result));
      } else if (range === 'lastMonth' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(result, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(result, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setPowerConsumedData(fillMissingLast24HrsGeneric(result));
      }
      setRevenuePowerSessions(response.data);

      setIsLoading(false);
    } catch (error) {
      console.error(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId && endConsumerType) {
      fetchPowerConsumedReport('last24hours');
    }
  }, [selectedEndConsumer?.userCustomId, endConsumerType]);

  return (
    <div className="total-revenue-chart-wrap" dir={i18n.dir()}>
      <div className="total-revenue-chart-header">
        <p className="header-title">{t('activityAndUsage')}</p>
        <div className="label-toggle-container">
          <CustomDateRangePicker
            inputLabel=''
            startDate={dateRange.start}
            endDate={dateRange.end}
            placeholder='DD-MM-YYYY - DD-MM-YYYY'
            onChange={(start, end) => {
              setDateRange({ start, end });
              // Placeholder: fetch default range or map dates to preset if needed
              fetchPowerConsumedReport('last12months');
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="revenue-consumer-loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <div className="total-revenue-chart-container">
            <div className="chart-wrapper">
              <Line data={chartData} options={options} height={250} />
            </div>
          </div>
          <div className="revenue-footer">
            <div className="color-border" style={{ background: '#FD4895' }} />
            <p className="title">{t('spend')}</p>
            <p className="value">
              {ToFixedValue(revenuePowerSessions?.totalRevenue) || 0} {t('SAR')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
